import { BetState } from '@/types/Bet';
import { type ClassValue, clsx } from 'clsx';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'absolute';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      textArea.remove();
      return Promise.resolve(true);
    } catch (error) {
      textArea.remove();
      return Promise.resolve(false);
    }
  }
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function parseJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export const getPlatform = (): 'Mobile' | 'Desktop' => {
  if (typeof window === 'undefined') return 'Desktop';

  const userAgent = navigator.userAgent.toLowerCase();

  const isMobile = /iphone|ipad|ipod|android|blackberry|opera mini|iemobile|mobile/i.test(
    userAgent
  );

  return isMobile ? 'Mobile' : 'Desktop';
};

export function formatMatchTime(isoString: string) {
  const date = dayjs(isoString);
  const now = dayjs();

  if (date.isSame(now, 'day')) {
    return { label: 'Today', time: date.format('hh:mm A') };
  }

  if (date.isSame(now.add(1, 'day'), 'day')) {
    return { label: 'Tomorrow', time: date.format('hh:mm A') };
  }

  if (date.diff(now, 'day') < 7) {
    return { label: date.format('dddd'), time: date.format('hh:mm A') };
  }

  return { label: date.format('DD MMM'), time: date.format('hh:mm A') };
}

export const getTeamShortName = (teamName?: string, maxLength?: number): string => {
  if (!teamName) return '';

  const shortName = teamName
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  return maxLength ? shortName.slice(0, maxLength) : shortName;
};

export const getDeltaExposure = (
  selectionId: number | string,
  marketId: number | string | undefined,
  betDetails: BetState
): number => {
  const bet = betDetails?.selectedBet;
  if (!bet || bet.marketId !== marketId) return 0;

  const isSameSelection = bet.selectionId == selectionId;
  const isBack = bet.betOn === 'BACK';
  const stake = betDetails?.stake || 0;

  /* ---------- BOOKMAKER ---------- */
  if (bet.marketCategory === 'BOOKMAKER') {
    if (isSameSelection) {
      const value = ((bet.rate || 0) / 100) * stake;
      return isBack ? value : -value;
    }
    return isBack ? -stake : stake;
  }

  /* ---------- NORMAL ---------- */
  if (bet.marketType === 'NORMAL') {
    if (isSameSelection) {
      const value = ((bet.rate || 0) - 1) * stake;
      return isBack ? value : -value;
    }
    return isBack ? -stake : stake;
  }

  /* ---------- FANCY / OTHER ---------- */
  if (isSameSelection) {
    const value = ((bet.fancyPercentage || 0) / 100) * stake;
    // return isBack ? value : -value;
    return 0;
  }

  return 0;
};

export const formatNumber = (value: number | string): string => {
  const num = Number(value);

  if (isNaN(num)) return '0';

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
  }).format(num);
};

export const getMarketRunnerStatus = (
  marketStatus?: string,
  runnerStatus?: string
): string | null => {
  const market = marketStatus?.toUpperCase();
  const runner = runnerStatus?.toUpperCase();

  const valid = ['OPEN', 'ACTIVE', ''];

  const isRunnerActive = valid.includes(runner ?? '');
  const isMarketActive = valid.includes(market ?? '');

  // ✅ both active
  if (isRunnerActive && isMarketActive) return null;

  // ✅ both NOT active → priority to runner
  if (!isRunnerActive && !isMarketActive) return runner ?? null;

  // ✅ only runner failed
  if (!isRunnerActive) return runner ?? null;

  // ✅ only market failed
  return market ?? null;
};

export function formatDateTime(
  value: string | number | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '';

  const date = new Date(value);

  // invalid date guard
  if (isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options,
  }).format(date);
}
